import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { AdminFetchFeeWithdrawalReport, AdminFetchFeeWithdrawals } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  FeeWithdrawalsPage,
  FetchFeeWithdrawalReportFailure,
  FetchFeeWithdrawalReportPayload,
  FetchFeeWithdrawalsPayload,
} from 'src/interfaces/feeWithdrawal'
import {
  buildFeeWithdrawalsQuery,
  isFeeWithdrawalReportNotFound,
  resolveFeeWithdrawalReportError,
} from 'src/utils/paymentsOps/feeWithdrawals'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

const PDF_MIME_TYPE = 'application/pdf'

export function* onFetchFeeWithdrawals(action: AsyncAction<FetchFeeWithdrawalsPayload>) {
  try {
    const { payload }: { payload: FeeWithdrawalsPage } = yield call(
      callSecureApi,
      Endpoints.AdminFetchFeeWithdrawals,
      { method: 'GET', query: buildFeeWithdrawalsQuery(action.payload) },
    )

    yield put(AdminFetchFeeWithdrawals.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.feeWithdrawals.onFetchFeeWithdrawals] error' })

    yield put(AdminFetchFeeWithdrawals.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load fee withdrawals'))
    }
  }
}

/**
 * The report streams a PDF behind the bearer token, so it is fetched here (not by a plain link)
 * and handed back through `next` as a `Blob` — never stored in Redux.
 */
export function* onFetchFeeWithdrawalReport(action: AsyncAction<FetchFeeWithdrawalReportPayload>) {
  const { feeWithdrawalId } = action.payload

  try {
    const { payload }: { payload: ArrayBuffer } = yield call(
      callSecureApi,
      Endpoints.AdminFetchFeeWithdrawalReport,
      {
        method: 'GET',
        params: [{ field: ':feeWithdrawalId', value: feeWithdrawalId }],
        returnBuffer: true,
      },
    )

    yield put(AdminFetchFeeWithdrawalReport.success({ feeWithdrawalId }))

    if (action.next) {
      action.next(null, new Blob([payload], { type: PDF_MIME_TYPE }))
    }
  } catch (error: unknown) {
    // A 404 is the expected "no report stored" answer, not an incident.
    if (!isFeeWithdrawalReportNotFound(error)) {
      captureSentryError(error, {
        message: '[saga.feeWithdrawals.onFetchFeeWithdrawalReport] error',
      })
    }

    const failure: FetchFeeWithdrawalReportFailure = { feeWithdrawalId, error }

    yield put(AdminFetchFeeWithdrawalReport.failed(failure))

    if (action.next) {
      action.next(resolveFeeWithdrawalReportError(error))
    }
  }
}

export function* feeWithdrawalsSaga() {
  yield takeLatest(AdminFetchFeeWithdrawals.type.REQUEST, onFetchFeeWithdrawals)
  yield takeEvery(AdminFetchFeeWithdrawalReport.type.REQUEST, onFetchFeeWithdrawalReport)
}
